import {
    createSlice,
    createEntityAdapter,
    createSelector,
    createAsyncThunk
} from '@reduxjs/toolkit';
import _ from "lodash";
import isEqual from "lodash/isEqual";
import apiClient from "../../../auth/apiClient";


const orgTreeAdapter = createEntityAdapter({
    selectId: (entity) => entity.entityid
});

export const loadOrgEntities = (entityID, federatedId) => async (dispatch) => {
    apiClient
        .get(`entities/${federatedId}/tree/byuser`)
        .then((resp) => {
            dispatch(setOrgTree({ entityID, entities: resp.data })); //load the full entity tree into EntityList
        })
        .catch((err) => {
            console.error(
                "Encountered error while loading all entities from context:",
                err
            );
        })
        .finally(() => {
            //do nothing, getting IDs next
        });
};

export const updateEntityInOrgTree = createAsyncThunk(
    'orgTree/updateEntity',
    async ({ entityId, limit = 100 }, thunkAPI) => {
        try {
            // Get the current entity tree from the API
            const response = await apiClient.get(`entities/${entityId}?limit=${limit}`);
            const entityTree = response.data;

            // Handle single entity or array response
            const entities = Array.isArray(entityTree)
                ? entityTree.flatMap(entity => flattenEntityTree(entity))
                : flattenEntityTree(entityTree);

            // Dispatch the action with the flattened entities array
            thunkAPI.dispatch(
                updateOrgTreeNoPermissions({
                    entities
                })
            )

            return entities;
        } catch (error) {
            console.error("Error updating entity tree:", error);
            throw error;
        }
    }
);

export const updateEntitySummary = createAsyncThunk(
    'orgTree/updateEntitySummary',
    async ({ entityID, userID }, thunkAPI) => {
        try {
            // Get the current entity tree from the API
            const response = await apiClient.get(`devicecontrol/entities/${entityID}/summary`);
            const permissionsResponse = await apiClient.get(
                `accounts/v2/permissions/${userID}?entityID=${entityID}`
            );
            const entitySummaries = _.cloneDeep(response.data);
            const permissions = _.cloneDeep(permissionsResponse.data);

            entitySummaries.forEach((entity) => {
                entity.permissions = permissions.find(
                    (permission) => permission.entityID === entity.entityid
                )?.permissions?.map((permission) => permission.permissionName) ?? null;
            });

            if(entitySummaries.length <= 0){
                return thunkAPI.rejectWithValue({
                    message: 'no entity summaries found'
                });
            }

            return {entitySummaries};
        } catch (error) {
            console.error("Error updating entity tree:", error);
            throw error;
        }
    }
);

/**
 * Recursively flatten the tree structure into an array of entities
 *
 * @param {Object} entity - The entity object with potential children
 * @returns {Array} - Flattened array of entities
 */
const flattenEntityTree = (entity) => {
    if (!entity) return [];

    // Start with the current entity
    let entities = [entity];

    // If the entity has children, recursively flatten and add them
    if (entity.haschildren && entity.children && Array.isArray(entity.children)) {
        entity.children.forEach(child => {
            entities = entities.concat(flattenEntityTree(child));
        });
    }

    return entities;
};



const orgTreeSlice = createSlice({
    name: "orgTree",
    initialState: orgTreeAdapter.getInitialState(),
    reducers: {
        setOrgTree: (state, action) => {
            orgTreeAdapter.setAll(state, action.payload.entities);
        },
        setOrgContext: (state, action) => {
            const {rootEntities} = action.payload;
            orgTreeAdapter.upsertMany(state, rootEntities);
        },
        updateOrgTree: (state, action) =>  {
            const {entities, childrenPermissions} = action.payload;
            let newEntities =  entities.filter((entity) => {
                if (entity.entitytype === 1 && !entity.parententityid && childrenPermissions[entity.entityid] == null) {
                    return false;
                }
                if (state.entities[entity.entityid] && state.entities[entity.entityid].permissions) {
                    return false;
                }
                return true;
            }).map((entity) => ({
                ...entity,
                permissions: childrenPermissions[entity.entityid] ?? entity.permissions
            }));
            orgTreeAdapter.upsertMany(state, newEntities);
        },
        updateOrgTreeNoPermissions: (state, action) =>  {
            const {entities} = action.payload;

            orgTreeAdapter.upsertMany(state, entities);
        },
        setNewOrgTree: (state, action) => {
            const {contextID, entities , permissions } = action.payload;
            if(entities.length <= 0) {
                return
            }
            let newEntities = [...entities]
            newEntities.forEach((entity) => {
                if(entity.entityid === contextID){
                    entity.permissions =       [
                        ...permissions[0].permissions?.map(
                            (permission) => permission.permissionName
                        ),
                        ...permissions[0].groups
                            ?.map((group) => [...group.permissions])
                            .flat(),
                    ] ?? [];
                }
            })
            // remove old tree
            orgTreeAdapter.removeAll(state);

            // set New Tree
            orgTreeAdapter.setAll(state, newEntities)
        },
        setNewNode: (state, action) => {
                orgTreeAdapter.addOne(state, action.payload);
        },
        removeAllNodes: (state) => {
            orgTreeAdapter.removeAll(state);
        },
        removeNode: (state, action) => {
            const entityId = action.payload;
            orgTreeAdapter.removeOne(state, entityId);
        },
        updateOrgEntity: (state, action) => {
            const updatedEntity = action.payload;
            const entityId = updatedEntity.entityid;

            if (!entityId) {
                console.error("No valid entity ID found in the payload:", updatedEntity);
                return;
            }

            const existingEntity = state.entities[entityId];

            existingEntity.name = action.payload.name;
            existingEntity.details = action.payload.details;
            existingEntity.settings = action.payload.settings;
        },
        setSetting: (state, action) => {
            const { entityid, settingName, settingValue } = action.payload;
            const foundEntity = state.entities[entityid];
            if (!foundEntity) return;
            const normalizedName = settingName.toLowerCase().replace("setting.", "");
            const setting = foundEntity.settings?.find(
                (x) => x.name.toLowerCase() === normalizedName
            );
            if (!setting) return;
            setting.value = settingValue;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(updateEntitySummary.fulfilled, (state, action) => {
                const {entitySummaries} = action.payload;
                console.log(entitySummaries)
                orgTreeAdapter.upsertMany(state, entitySummaries);
            })
    }
});

const OrgTreeSelectors = orgTreeAdapter.getSelectors((state) => state.orgTree);

export const {
    selectById: selectOrgTreeEntityById, // Retrieve a single entity by its ID
    selectAll: selectAllOrgTreeEntities, //Retrieve all entities as an array
} = OrgTreeSelectors;

export const selectOrgRawIDsWithScope = createSelector(
    selectAllOrgTreeEntities,
    (entities) => {
        return entities.map((entity) => ({
            entityid: entity.entityid,
            parententityid: entity.parententityid || null, // Return null if no parentEntityId;
            name: entity.name,
        }));
    }
);
export const selectEntities = (state) => state.orgTree.entities;
const attachChildren = (entities, entity) => {
    // Find the children of the current entity
    const children = Object.values(entities)
        .filter(
            (child) =>
                child.parententityid === entity.entityid
        )
        .map((child) => attachChildren(entities, child)); // Recursively attach children to each child

    return {
        ...entity,
        children,
    };
};
export const selectSpecificEntitiesWithChildren = (specificEntities) =>
    createSelector([selectEntities], (entities) => {
        return specificEntities.map((entity) => attachChildren(entities, entity));
    });

export const selectAllOrgFacilities = createSelector(
    selectAllOrgTreeEntities,
    (entities) => entities.filter((entity) => entity.type === "Facility")
);

export const selectAllOrgDevicesWithFacilityName = createSelector(
	selectAllOrgTreeEntities,
	(entities) => {
		var deviceEntities = entities.filter((entity) => entity.typeid === 3).map(device =>(
			{
				deviceId: device.entityid,
				deviceName: device.name,
				facilityName: device.facilityname
			}
		));
		return deviceEntities
	}
);

export const selectAllOrgScopedPermissions = createSelector(
    (state) => state.orgTree, // Access the normalized state slice
    (coreEntities) => {
        const result = {};

        // Iterate through the IDs to access each entity
        coreEntities.ids.forEach((id) => {
            const entity = coreEntities.entities[id];
            if (entity && entity.permissions && entity.permissions.length > 0) {
                result[id] = entity.permissions
            }
        });

        return result;
    }
);

export const selectOrgEntitiesWithNoState = createSelector(
    (state) => state.orgTree, // Access normalized entities
    (entities) => {
        const result = {};
        for (const [key, entity] of Object.entries(entities.entities)) {
            result[key] = {
                entityId: entity.entityid,
                parentEntityId: entity.parententityid || null,
                typeId: entity.typeid,
            };
        }
        if (!selectOrgEntitiesWithNoState.previousResult) {
            selectOrgEntitiesWithNoState.previousResult = result;
            return result;
        }

        // Return the previous result if nothing has changed
        if (isEqual(result, selectOrgEntitiesWithNoState.previousResult)) {
            return selectOrgEntitiesWithNoState.previousResult;
        }

        selectOrgEntitiesWithNoState.previousResult = result;
        return Object.keys(result).length === 0 ? null : result;
    }
);


const reducedOrgTreeData = createSelector(
    [
        (state) => state.orgTree.entities,
        (state) => state.orgTree.ids],
    (entities,id) => {
        const result = {};
        for (const [key, entity] of Object.entries(entities)) {
            result[key] = {
                entityid: entity.entityid,
                parententityid: entity.parententityid || null,
                typeid: entity.typeid,
                name: entity.name
            };
        }
        return {entities: result, ids: id};
    }
);

export const selectOrgTreeData = createSelector(
    [reducedOrgTreeData],
    ({entities, ids}) => {
        // Find the root entity
        const rootEntity = Object.values(entities)
            .find(entity => !entity.parententityid);

        if (!rootEntity) {
            return null;
        }
        // Build tree from root
        const buildTree = (entityId) => {
            const entity = entities[entityId];
            if (!entity) return null;

            // Find direct children
            const directChildren = ids
                .map(id => entities[id])
                .filter(e => e && e.parententityid === entityId);


            return {
                ...entity,
                children: directChildren
                    .map(child => buildTree(child.entityid))
                    .filter(Boolean)
            };
        };
        const tree = buildTree(rootEntity.entityid);
        // Start building from root
        return tree;
    }
);

export const selectEntityForOrgTree = (entityId) => createSelector(
    (state) => state.orgTree.entities,
    (entities) => {
        const entity = entities[entityId];

        const hasChildren = Object.values(entities).some((e) => e.parententityid === entity?.entityid);
        const deviceMode = _.find(entity?.settings ?? [], { name: "devicemode" })?.value ?? null;
        const disabled = deviceMode?.toLowerCase() === "door"
            ? String(_.find(entity?.settings ?? [], { name: "devicedisabled" })?.value) === "true"
            : entity?.state?.Disabled ?? false;
        return {
            parentEntityID: entity?.parententityid,
            entityID: entity?.entityid,
            name: entity?.name,
            typeID: entity?.typeid,
            hasChildren,
            lotFull:
                _.find(entity?.settings ?? [], { name: "displaylotfull" })?.value ==
                "auto"
                    ? entity?.state != null && entity?.state?.Occupancy?.Full == true //if auto, is occupancy full or not?
                    : _.find(entity?.settings ?? [], { name: "displaylotfull" })
                        ?.value == "on"
                        ? true
                        : false, //if manual, is it on or not?
            laneClosed:
                String(_.find(entity?.settings ?? [], { name: "laneclosedsign" })?.value) === "true",
            disabled,
            deviceMode,
            reverseOffline: entity?.state?.ReversingLane?.ReverseOffline,
            depth: entity?.treedepth
        };
    }
);



export const makeOrgParentEntitySelector = (entityId) =>
    createSelector(
        (state) => state.orgTree, // Access the coreEntities slice
        ({ entities }) => {
            const parentEntityId = entities[entityId]?.parententityid;
            return entities[parentEntityId] ?? {};
        }
    );
export const selectOrgParentEntity = (entityId) => (state) => makeOrgParentEntitySelector(entityId)(state);
export const {
setNewNode, // might use this in the future not sure yet - JDP
updateOrgTree,
setNewOrgTree,
updateOrgEntity,
setOrgTree,
setOrgContext,
updateOrgTreeNoPermissions,
removeNode,
setSetting: orgSetSettingSync
} = orgTreeSlice.actions;

export default orgTreeSlice.reducer;